import React, { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { HOME_PAGE } from "@/constants/testIds";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function useCountUp(target, durationMs = 800, run = false) {
  const [value, setValue] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!run) return;
    if (reduce) {
      setValue(target);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const elapsed = t - start;
      const pct = Math.min(elapsed / durationMs, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - pct, 3);
      setValue(Math.round(target * eased));
      if (pct < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, run, reduce]);
  return value;
}

function Stat({ value, suffix = "", label, testid, run, asText }) {
  const display = useCountUp(value, 800, run);
  return (
    <div
      data-testid={testid}
      className="min-w-[210px] md:min-w-0 px-7 md:px-0"
      style={{
        flex: "0 0 auto",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: "clamp(40px, 4.2vw, 56px)",
          lineHeight: 1,
          letterSpacing: "-0.01em",
          color: "var(--rk-ink)",
        }}
      >
        {asText ? asText : `${display}${suffix}`}
      </div>
      <div
        className="mt-3 rk-eyebrow"
        style={{ color: "var(--rk-muted)" }}
      >
        {label}
      </div>
    </div>
  );
}

export default function ProofBar() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section
      ref={ref}
      data-testid={HOME_PAGE.proofBar}
      aria-label="Proof and credentials"
      style={{
        backgroundColor: "var(--rk-surface)",
        borderTop: "1px solid var(--rk-copper)",
        borderBottom: "1px solid var(--rk-copper)",
      }}
    >
      <div
        className="rk-hscroll md:overflow-visible"
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
          padding: "36px 0",
        }}
      >
        <div
          className="flex md:grid md:grid-cols-6 md:gap-0 md:px-0 md:mx-auto rk-container-wide"
          style={{ width: "100%" }}
        >
          <Stat
            value={400}
            suffix="+"
            label="Products launched"
            testid={HOME_PAGE.statProducts}
            run={inView}
          />
          <div className="hidden md:block" style={{ borderLeft: "1px solid var(--rk-border-strong)" }} />
          <Stat
            value={20}
            suffix="+"
            label="Years"
            testid={HOME_PAGE.statYears}
            run={inView}
          />
          <div className="hidden md:block" style={{ borderLeft: "1px solid var(--rk-border-strong)" }} />
          <Stat
            asText="Fortune 500"
            label="Federal · Global NGOs"
            testid={HOME_PAGE.statSectors}
            run={inView}
          />
          <div className="hidden md:block" style={{ borderLeft: "1px solid var(--rk-border-strong)" }} />
          <Stat
            asText="Salesforce MVP"
            label="Hall of Fame"
            testid={HOME_PAGE.statMvp}
            run={inView}
          />
          <div className="hidden md:block" style={{ borderLeft: "1px solid var(--rk-border-strong)" }} />
          <Stat
            asText="CES 2025/26"
            label="Speaker"
            testid={HOME_PAGE.statCes}
            run={inView}
          />
          <div className="hidden md:block" style={{ borderLeft: "1px solid var(--rk-border-strong)" }} />
          <Stat
            asText="White House"
            label="Apple · NASA"
            testid={HOME_PAGE.statClients}
            run={inView}
          />
        </div>
      </div>
    </section>
  );
}
